/**
 * The three status banners. All three exist to make a silent state loud.
 *
 * · {@link OfflineBanner} — offline is a NORMAL state for this app, not an error, and the wording says
 *   so: work is still being saved. A red "connection lost" banner would teach a technician that the
 *   app is broken in a locker bay, and they would stop using it exactly when it is most useful.
 * · {@link StorageBanner} — reports `navigator.storage.persist()`'s real answer. When the browser
 *   REFUSED, this says so in those words. It is the single most important honest surface in the app;
 *   see `src/core/storage.ts`.
 * · {@link AuthStubBanner} — says out loud that sign-in is not built, so nobody demoing this can
 *   mistake the app for authenticated.
 *
 * **These strings are hardcoded English on purpose, for the Phase 1 pilot** — a decision, not an
 * oversight. See `ENGLISH_ONLY_PILOT_DECISION` in `src/i18n/index.ts` for what was decided, why
 * (PRD Phase 1 says English only), and what would reopen it.
 */

import type { StorageAssessment } from '../core/storage';
import type { TokenProvider } from '../core/auth';

export function OfflineBanner({ online, queued }: { online: boolean; queued: number }): JSX.Element | null {
  if (online) {
    return null;
  }
  return (
    <div className="banner banner--offline" role="status">
      <p className="banner__title">No connection — you can keep working</p>
      <p className="banner__body">
        {queued === 0
          ? 'Anything you submit is saved on this phone and sent automatically when you get signal.'
          : `${queued} ${queued === 1 ? 'submission is' : 'submissions are'} saved on this phone and will be sent automatically when you get signal.`}
      </p>
    </div>
  );
}

export function StorageBanner({ assessment }: { assessment: StorageAssessment | null }): JSX.Element | null {
  if (assessment === null) {
    // Still asking. Nothing is claimed either way — an optimistic "storage is safe" that flips to a
    // warning a second later is the exact ambiguity this banner is meant to remove.
    return null;
  }
  if (assessment.durable) {
    // Deliberately quiet when the answer is good: a persistent green banner is a banner nobody reads,
    // which is a problem on the day it turns into a warning. The Settings view shows the detail.
    return null;
  }
  return (
    <div className="banner banner--danger" role="alert">
      <p className="banner__title">
        {assessment.persistence === 'not-persisted'
          ? 'This phone did not agree to protect saved work'
          : 'Cannot confirm this phone will keep saved work'}
      </p>
      <p className="banner__body">{assessment.message}</p>
    </div>
  );
}

export function AuthStubBanner({ provider }: { provider: TokenProvider }): JSX.Element | null {
  if (provider.kind !== 'stub') {
    return null;
  }
  return (
    <div className="banner banner--info" role="status">
      <p className="banner__title">Sign-in is not built yet</p>
      <p className="banner__body">
        No requests are being sent to the server. Work you submit is saved on this device and stays in
        the outbox. Sign-in is blocked on the Federate service profile for the IdPrism pool (OQ-5).
      </p>
    </div>
  );
}
